import React, { createContext, FC, useContext, useEffect, useRef, useState } from "react";
import { ColorSet } from "../interfaces";
import { UserContext } from "@app/user";
import { FeatureFlagContext, Features } from "@app/feature-flags";

export interface EcdColorsContextValues {
    arrowPositive: string;
    arrowNegative: string;
    barHoverBackground: string;
    defaultColors: ColorSet;
    exceptionsArray: string[];
    exceptionsPrimary: string;
    fillableGapsPrimary: string;
    fillableGapsSecondary: string;
    gapsArray: string[];
    gapsPrimary: string;
    getNewExceptionsArray: (exceptionsPrimary: string) => string[];
    getNewGapsArray: (gapsPrimary: string) => string[];
    unfillableGaps: string;
    updateColors: (newColorSet: ColorSet) => void;
}

const initialValues: EcdColorsContextValues = {
    arrowNegative: "#cc3a52", // $qes-primary-red
    arrowPositive: "#056edf", // $qes-primary-blue
    barHoverBackground: "#f0f0f0",
    defaultColors: undefined,
    exceptionsArray: ["#056edf", "#4d97e6", "#7cb1e9", "#adcdef", "#dde8f3"],
    exceptionsPrimary: "#056edf", // $qes-primary-blue
    fillableGapsPrimary: "#1c7c4c",
    fillableGapsSecondary: "#a9eea7",
    gapsArray: ["#402294", "#724cde", "#9e83e8", "#c9bdea"],
    gapsPrimary: "#402294",
    getNewExceptionsArray: undefined,
    getNewGapsArray: undefined,
    unfillableGaps: "#bcbcbc",
    updateColors: () => null,
}

export const EcdColorsContext = createContext(initialValues);

const EcdColorsContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
    const [arrowPositive, setArrowPositive] = useState(initialValues.arrowPositive);
    const [arrowNegative, setArrowNegative] = useState(initialValues.arrowNegative);
    const [barHoverBackground, setBarHoverBackground] = useState(initialValues.barHoverBackground);
    const [exceptionsPrimary, setExceptionsPrimary] = useState(initialValues.exceptionsPrimary);
    const [exceptionsArray, setExceptionsArray] = useState(initialValues.exceptionsArray);
    const [fillableGapsPrimary, setFillableGapsPrimary] = useState(initialValues.fillableGapsPrimary);
    const [fillableGapsSecondary, setFillableGapsSecondary] = useState(initialValues.fillableGapsSecondary);
    const [gapsPrimary, setGapsPrimary] = useState(initialValues.gapsPrimary);
    const [gapsArray, setGapsArray] = useState(initialValues.gapsArray);
    const [unfillableGaps, setUnfillableGaps] = useState(initialValues.unfillableGaps);

    const userContext = useContext(UserContext);
    const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
    const localStorageKey = useRef(`${userContext.userId}__enterprise-compliance-dashboard-colors`).current;

    // get colors from storage on mount if applicable
    useEffect(() => {
        if (featureFlags.has(Features.ECD_COLOR_CUSTOMIZATION)) {
            const savedColors = window.localStorage.getItem(localStorageKey);
            if (savedColors) {
                updateColors(JSON.parse(savedColors));
            }
        }
    }, []);

    // exceptions primary changed, so update the rest of the gradient
    useEffect(() => {
        setExceptionsArray(getNewExceptionsArray(exceptionsPrimary));
    }, [exceptionsPrimary]);

    // gaps primary changed, so update the rest of the gradient
    useEffect(() => {
        setGapsArray(getNewGapsArray(gapsPrimary));
    }, [gapsPrimary]);

    const defaultColors = {
        arrowNegative: initialValues.arrowNegative,
        arrowPositive: initialValues.arrowPositive,
        barHoverBackground: initialValues.barHoverBackground,
        exceptionsArray: initialValues.exceptionsArray,
        exceptionsPrimary: initialValues.exceptionsPrimary,
        fillableGapsPrimary: initialValues.fillableGapsPrimary,
        fillableGapsSecondary: initialValues.fillableGapsSecondary,
        gapsArray: initialValues.gapsArray,
        gapsPrimary: initialValues.gapsPrimary,
        unfillableGaps: initialValues.unfillableGaps,
    };

    const getNewExceptionsArray = (exceptionsPrimary: string) => {
        const { h, s, l } = convertHexToHSL(exceptionsPrimary);
        const newExceptionsArray = [
            exceptionsPrimary,
            `hsl(${h} ${s}% ${l + 12}%)`,
            `hsl(${h} ${s}% ${l + 24}%)`,
            `hsl(${h} ${s}% ${l + 36}%)`,
            `hsl(${h} ${s}% ${l + 48}%)`,
        ];
        return newExceptionsArray;
    };

    const getNewGapsArray = (gapsPrimary: string) => {
        const { h, s, l } = convertHexToHSL(gapsPrimary);
        const newGapsArray = [
            gapsPrimary,
            `hsl(${h} ${s}% ${l + 21.5}%)`,
            `hsl(${h} ${s}% ${l + 34.8}%)`,
            `hsl(${h} ${s}% ${l + 48}%)`,
        ];
        return newGapsArray;
    }

    const updateColors = (newColorSet: ColorSet) => {
        setArrowNegative(newColorSet.arrowNegative);
        setArrowPositive(newColorSet.arrowPositive);
        setBarHoverBackground(newColorSet.barHoverBackground);
        setExceptionsPrimary(newColorSet.exceptionsPrimary);
        setFillableGapsPrimary(newColorSet.fillableGapsPrimary);
        setFillableGapsSecondary(newColorSet.fillableGapsSecondary);
        setGapsPrimary(newColorSet.gapsPrimary);
        setUnfillableGaps(newColorSet.unfillableGaps);
        if (featureFlags.has(Features.ECD_COLOR_CUSTOMIZATION)) {
            window.localStorage.setItem(localStorageKey, JSON.stringify(newColorSet));
        }
    }

    /* adapted from a blog post on csstricks.com */
    function convertHexToHSL(hex: string) {
        // Convert hex to RGB first
        const hexValue = hex.substring(1);
        let r = 0, g = 0, b = 0;
        if (hexValue.length == 3) {
            r = parseInt(`${hexValue.substring(0, 1)}${hexValue.substring(0, 1)}`, 16);
            g = parseInt(`${hexValue.substring(1, 1)}${hexValue.substring(1, 1)}`, 16);
            b = parseInt(`${hexValue.substring(2, 1)}${hexValue.substring(2, 1)}`, 16);
        } else if (hexValue.length == 6) {
            r = parseInt(hexValue.substring(0, 2), 16);
            g = parseInt(hexValue.substring(2, 4), 16);
            b = parseInt(hexValue.substring(4), 16);
        }

        // Then to HSL
        r /= 255;
        g /= 255;
        b /= 255;
        const cmin = Math.min(r, g, b),
            cmax = Math.max(r, g, b),
            delta = cmax - cmin;
        let h = 0,
            s = 0,
            l = 0;

        if (delta == 0)
            h = 0;
        else if (cmax == r)
            h = ((g - b) / delta) % 6;
        else if (cmax == g)
            h = (b - r) / delta + 2;
        else
            h = (r - g) / delta + 4;

        h = Math.round(h * 60);

        if (h < 0)
            h += 360;

        l = (cmax + cmin) / 2;
        s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
        s = +(s * 100).toFixed(1);
        l = +(l * 100).toFixed(1);

        /* eslint-disable-next-line sort-keys */
        return { h, s, l };
    }

    return (
        <EcdColorsContext.Provider
            value={{
                arrowNegative,
                arrowPositive,
                barHoverBackground,
                defaultColors,
                exceptionsArray,
                exceptionsPrimary,
                fillableGapsPrimary,
                fillableGapsSecondary,
                gapsArray,
                gapsPrimary,
                getNewExceptionsArray,
                getNewGapsArray,
                unfillableGaps,
                updateColors,
            }}>
            {props.children}
        </EcdColorsContext.Provider>
    )
}

export default EcdColorsContextProvider;